import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Hospital } from './hospitalSlice';
import api from '../common/api';

export interface User {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  hospital?: Hospital;
  password?: string;
}

export interface UserState {
  users: User[];
  doctors: User[];
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  doctors: [],
  loading: false,
  error: null,
};

const messageFromError = (error: any): string => {
  if (error.response) {
    // Server responded with a status other than 200 range
    return error.response.data.message;
  } else if (error.request) {
    // Request was made but no response received
    return 'No response from server';
  } else {
    // Something else happened while setting up the request
    return error.message;
  }
};

export const fetchUsers = createAsyncThunk<User[], void, { rejectValue: string }>('users/fetchUsers', async (_, thunkAPI) => {
  try {
    const response = await api.get('/api/v1/users');
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to load users - ${messageFromError(error)}`);
  }
});

export const fetchDoctorsByHospital = createAsyncThunk<User[], string, { rejectValue: string }>(
  'users/fetchDoctorsByHospital',
  async (hospitalId, thunkAPI) => {
    try {
      const response = await api.get(`/api/v1/users/doctors/hospital/${hospitalId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(`Failed to load doctors - ${messageFromError(error)}`);
    }
  },
);

export const createUser = createAsyncThunk<User, User, { rejectValue: string }>('users/createUser', async (userData, thunkAPI) => {
  try {
    const response = await api.post('/api/v1/users', {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: userData.role,
      password: userData.password,
      hospitalId: userData.hospital?.id,
    });
    void thunkAPI.dispatch(fetchUsers());
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(`Failed to create user - ${error.response.data.message}`);
  }
});

export const updateUser = createAsyncThunk<User, User, { rejectValue: string }>('users/updateUser', async (userData, thunkAPI) => {
  try {
    const response = await api.patch(`/api/v1/users/${userData.id}`, {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: userData.role,
      hospitalId: userData.hospital?.id,
    });
    void thunkAPI.dispatch(fetchUsers());
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(`Failed to update user - ${messageFromError(error)}`);
  }
});

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch users';
      })
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to create user';
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.users.push(action.payload);
        state.loading = false;
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update user';
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
        const index = state.users.findIndex((user) => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(fetchDoctorsByHospital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorsByHospital.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.doctors = action.payload;
        state.loading = false;
      })
      .addCase(fetchDoctorsByHospital.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch doctors';
      });
  },
});

export default usersSlice.reducer;
